import { coerceNumberProperty, NumberInput } from '@angular/cdk/coercion';
import { DOCUMENT } from '@angular/common';
import {
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  OnDestroy,
  output,
} from '@angular/core';

export enum StickyWhen {
  Intersecting,
  NotIntersecting,
}

@Directive({
  selector: '[blazeSticky]',
  exportAs: 'blazeSticky',
})
export class StickyDirective implements OnDestroy {
  private static readonly THRESHOLDS = Array.from({ length: 101 }, (_, i) => i / 100);

  private readonly document = inject<Document>(DOCUMENT);
  private readonly elementRef = inject(ElementRef);
  private readonly ngZone = inject(NgZone);

  stickTo = input<'top' | 'bottom'>('top');
  stickyWhen = input(StickyWhen.Intersecting);
  scrollContainer = input<string | HTMLElement | null>(null);
  triggerOn = input<string | HTMLElement | null>(null);
  blazeStickyEnabled = input(true);
  startOffset = input(0, {
    alias: 'blazeStickyStartOffset',
    transform: (v: NumberInput) => coerceNumberProperty(v, 0),
  });
  endOffset = input(0, {
    alias: 'blazeStickyEndOffset',
    transform: (v: NumberInput) => coerceNumberProperty(v, 0),
  });
  positionOffset = input(0, {
    alias: 'blazeStickyPositionOffset',
    transform: (v: NumberInput) => coerceNumberProperty(v, 0),
  });
  stickyChange = output<{ isSticky: boolean }>();

  private scrollContainerElement: HTMLElement | null = null;
  private triggerOnElement: HTMLElement | null = null;
  private replacementElement: HTMLElement | null = null;
  private intersectionObserver: IntersectionObserver | null = null;
  private isSticky = false;

  constructor() {
    effect(() => {
      const value = this.scrollContainer();
      const element =
        typeof value === 'string' ? this.document.querySelector<HTMLElement>(value) : value;

      this.scrollContainerElement = element;

      if (element) {
        this.removeReplacementElement();
        this.replacementElement = this.createReplacement();
        element.classList.add('blaze-sticky');
        element.insertAdjacentElement('afterend', this.replacementElement);
      }
    });

    effect(() => {
      const value = this.triggerOn();
      const element =
        typeof value === 'string' ? this.document.querySelector<HTMLElement>(value) : value;

      if (element && element !== this.triggerOnElement) {
        this.triggerOnElement = element;
        this.attachIntersectionObserver(element);
      }
    });

    effect(() => {
      const enabled = this.blazeStickyEnabled();

      if (enabled && this.triggerOnElement) {
        const isIntersecting = this.isIntersecting(this.triggerOnElement.getBoundingClientRect());
        this.onIntersection(isIntersecting);
      } else if (this.scrollContainerElement) {
        this.updateStyles('normal');
        this.isSticky = false;
        this.stickyChange.emit({ isSticky: false });
      }
    });

    effect(() => {
      const offset = this.positionOffset();

      if (this.isSticky && this.scrollContainerElement) {
        this.scrollContainerElement.style[this.stickTo()] = `${offset}px`;
      }
    });
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.removeReplacementElement();
  }

  private attachIntersectionObserver(triggerOnElement: HTMLElement): void {
    this.ngZone.runOutsideAngular(() => {
      this.intersectionObserver?.disconnect();
      this.intersectionObserver = new IntersectionObserver(
        (entries) => {
          if (!this.blazeStickyEnabled()) {
            return;
          }
          const entry = entries[entries.length - 1];
          const isIntersecting = this.isIntersecting(entry.boundingClientRect);
          const shouldBeSticky =
            (isIntersecting && this.stickyWhen() === StickyWhen.Intersecting) ||
            (!isIntersecting && this.stickyWhen() === StickyWhen.NotIntersecting);
          if (shouldBeSticky !== this.isSticky) {
            this.ngZone.run(() => this.onIntersection(isIntersecting));
          }
        },
        { threshold: StickyDirective.THRESHOLDS },
      );
      this.intersectionObserver.observe(triggerOnElement);
    });
  }

  private isIntersecting(rect: DOMRectReadOnly): boolean {
    const { top, height, bottom } = rect;
    if (this.stickTo() === 'top') {
      return top + this.startOffset() <= 0 && Math.abs(top) <= height - this.endOffset();
    }
    return (
      this.document.documentElement.clientHeight - top + this.startOffset() >= 0 &&
      bottom - this.document.documentElement.clientHeight >= this.endOffset()
    );
  }

  private onIntersection(isIntersecting: boolean): void {
    let mode: 'floating' | 'normal';
    if (
      (isIntersecting && this.stickyWhen() === StickyWhen.Intersecting) ||
      (!isIntersecting && this.stickyWhen() === StickyWhen.NotIntersecting)
    ) {
      mode = 'floating';
    } else {
      mode = 'normal';
    }
    this.updateStyles(mode);
    this.isSticky = mode === 'floating';
    this.stickyChange.emit({ isSticky: this.isSticky });
  }

  private createReplacement(): HTMLElement {
    const element = this.document.createElement('div');
    element.style.display = 'none';

    return element;
  }

  private updateStyles(mode: 'floating' | 'normal'): void {
    if (!this.replacementElement || !this.scrollContainerElement) {
      return;
    }

    if (mode === 'floating') {
      this.scrollContainerElement.classList.add('blaze-sticky--active');
      this.scrollContainerElement.style[this.stickTo()] = `${this.positionOffset()}px`;
      this.scrollContainerElement.style.position = 'fixed';

      const { height } = this.elementRef.nativeElement.getBoundingClientRect();
      this.replacementElement.style.display = '';
      this.replacementElement.style.height = `${height}px`;
    } else {
      this.scrollContainerElement.style[this.stickTo()] = '';
      this.replacementElement.style.display = 'none';
      this.scrollContainerElement.classList.remove('blaze-sticky--active');
      this.scrollContainerElement.style.position = '';
    }
  }

  private removeReplacementElement(): void {
    if (!this.replacementElement) {
      return;
    }
    this.replacementElement.parentElement?.removeChild(this.replacementElement);
  }
}
